import { useContext, useEffect, useRef, useState } from 'react';
import AppContext from '../contexts/AppContext';
import { xfdfApi, XfdfModel } from '../utils/api/xfdf';
import tenantApi from '../utils/api/tenant';

type SaveAnnotationModel = Omit<
  XfdfModel,
  'uploadId' | 'createdByUserId' | 'id'
> & {
  isModificationLog?: boolean;
  action?: string;
};

export type AnnotationApi = {
  authorName: string;
  authorRole: string;
  data: XfdfModel[];
  isAppendOnly: boolean;
  /**
   * Resolves with the tenant's append-only setting once it has been fetched.
   * `isAppendOnly` starts false and only updates on a later render, so a
   * callback registered on mount must await this rather than read the field.
   */
  resolveIsAppendOnly?: () => Promise<boolean>;
  loading: boolean;
  index(): Promise<XfdfModel[]>;
  save(model: SaveAnnotationModel): Promise<void>;
};

/**
 * Provides api and manages state for XFDF annotations
 */
export function useAnnotationApi(
  uploadId: number,
  tenantId?: number,
  projectId?: number,
): AnnotationApi {
  const authorName = useAnnotationAuthorName();
  const authorRole = useAnnotationAuthorRole();
  const [data, setData] = useState<XfdfModel[] | null>(null);
  const [isAppendOnly, setIsAppendOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const index = async () => {
    const nextData = await xfdfApi.index(uploadId, projectId);

    setData(nextData);

    return nextData;
  };

  // The ref object is stable across renders, so a callback captured on the
  // first render still sees the lookup started by the mount effect.
  const appendOnlyRef = useRef<Promise<boolean>>(Promise.resolve(false));

  const setAppendOnly = async () => {
    const value = await tenantApi.fetchIsAppendOnly(tenantId!, projectId);

    setIsAppendOnly(value);

    return value;
  };

  const save = async (model: SaveAnnotationModel) => {
    await xfdfApi.save({ ...model, uploadId });
  };

  const api: AnnotationApi = {
    authorName,
    authorRole,
    data: data || [],
    isAppendOnly,
    resolveIsAppendOnly: () => appendOnlyRef.current,
    loading,
    index,
    save,
  };

  useEffect(() => {
    index();
    if (tenantId) {
      // A failed lookup keeps the old behaviour (not append-only) rather than
      // blocking the annotation import that awaits it.
      appendOnlyRef.current = setAppendOnly().catch(() => false);
    }
    xfdfApi.initSocket((err: any) => {
      throw err;
    });

    return () => {
      xfdfApi.stopSocket();
    };
  }, []);

  useEffect(() => {
    if (loading && data) {
      setLoading(false);
    }
  }, [loading, data]);

  return api;
}

export function useAnnotationAuthorName() {
  const appStore = useContext(AppContext);

  const user = appStore.get('user');

  if (user && user.email) {
    return user.email;
  }

  return 'Guest';
}

export function useAnnotationAuthorRole() {
  const appStore = useContext(AppContext);

  const user = appStore.get('user');

  if (user && user.userTenantRoles && user.userTenantRoles.length > 0) {
    return user.userTenantRoles[0].role.name;
  }

  return 'Guest';
}
